import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import UpcomingOutlinedIcon from '@mui/icons-material/UpcomingOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import StatCard from '../components/StatCard';
import ChartBlock from '../components/ChartBlock';
import TimelineChart from '../components/TimelineChart';
import DonutChart from '../components/DonutChart';
import RankList from '../components/RankList';
import Heatmap from '../components/Heatmap';
import styles from '../components/stats.module.css';
import palette from '../components/palette';
import { formatNumber, granularityHint } from '../utils/format';
import { goalHint } from '../utils/goal';
import { useStatsPrint } from '../components/printContext';
import { HELP } from '../utils/help';
import { NO_COMPARE } from '../utils/periods';

export const SCHEDULE_CARDS = [
  { key: 'total', label: 'Total de horários', icon: <EventNoteOutlinedIcon />, accent: 'blue', help: HELP.total },
  { key: 'done', label: 'Realizados', icon: <EventAvailableOutlinedIcon />, accent: 'green', help: HELP.done },
  { key: 'upcoming', label: 'Ainda por vir', icon: <UpcomingOutlinedIcon />, accent: 'purple', help: HELP.upcoming },
  { key: 'expected', label: 'Previsto', icon: <FlagOutlinedIcon />, accent: 'teal', goal: true, help: HELP.expected },
  { key: 'noshow', label: 'Faltas', icon: <EventBusyOutlinedIcon />, inverse: true, accent: 'amber' },
  { key: 'noshowRate', label: 'Taxa de faltas', icon: <PercentOutlinedIcon />, type: 'percent', inverse: true, accent: 'amber', help: HELP.noshowRate },
  { key: 'canceled', label: 'Cancelados', icon: <BlockOutlinedIcon />, inverse: true, accent: 'pink' },
  { key: 'cancelRate', label: 'Taxa de cancelamento', icon: <EventRepeatOutlinedIcon />, type: 'percent', inverse: true, accent: 'pink' },
];

/**
 * Estados dos atendimentos no gráfico do período; a legenda filtra cada um.
 * Reagendados começam escondidos: o atendimento já aparece no horário novo.
 */
export const SCHEDULE_SERIES = [
  { key: 'done', label: 'Realizados', color: palette.blue, stack: 'total' },
  { key: 'upcoming', label: 'Ainda por vir', color: palette.blueSoft, stack: 'total' },
  { key: 'noshow', label: 'Faltas', color: palette.amber, stack: 'total' },
  { key: 'canceled', label: 'Cancelados', color: palette.pink, stack: 'total' },
  { key: 'rescheduled', label: 'Reagendados', color: palette.gray, stack: 'total', hidden: true },
];

/** Soma no balão do gráfico: o que já foi feito mais o que ainda está marcado. */
export const SCHEDULE_TOTAL = { label: 'Previsto (realizados + por vir)', keys: ['done', 'upcoming'] };

/** Sem os reagendados: o total bate com "Total de horários" e cada atendimento conta uma vez. */
export function outcomeItems(outcomes) {
  return [
    { label: 'Realizados', value: outcomes.done, color: palette.blue },
    { label: 'Ainda por vir', value: outcomes.upcoming, color: palette.blueSoft },
    { label: 'Faltas', value: outcomes.noshow, color: palette.amber },
    { label: 'Cancelados pela clínica', value: outcomes.canceledClinic, color: palette.pink },
    { label: 'Cancelados pelo paciente', value: outcomes.canceledPatient, color: palette.purple },
  ];
}

const rescheduledHint = (count) => (
  count > 0 ? `${formatNumber(count)} ${count === 1 ? 'remarcação fica' : 'remarcações ficam'} de fora: vale o horário novo` : undefined
);

export function resourceItems(byResource) {
  return byResource.map((item) => ({
    label: item.label,
    value: item.done + item.upcoming,
    display: formatNumber(item.done + item.upcoming),
    sub: item.noshow ? `${item.noshow} ${item.noshow === 1 ? 'falta' : 'faltas'}` : '',
  }));
}

/**
 * Dica do card "Previsto": quanto falta para a meta; sem meta definida pela
 * clínica, um link discreto para defini-la em Configurações (fora do PDF).
 */
function expectedHint(kpi, goal, period, print) {
  if (goal) return goalHint(kpi && kpi.value, goal, period);
  return print ? undefined : <Link to="/company/preferences" className={styles.goalLink}>Definir meta</Link>;
}

/**
 * Dica do card "Ainda por vir": até quando o período enxerga o futuro e, num
 * período que já passou, como olhar para a frente. Fica de fora no PDF e na
 * comparação (o valor comparado ocupa a mesma linha).
 */
function upcomingHint(period, print) {
  if (print || !period || (period.compare && period.compare !== NO_COMPARE)) return undefined;
  const today = dayjs().format('YYYY-MM-DD');
  if (period.end < today) return 'Período já passou: escolha Próximos 30 dias ou Próximo mês';
  return `Marcados até ${dayjs(period.end).format('DD/MM/YYYY')}`;
}

function cardHint(key, hasGoal, kpi, goal, period, print) {
  if (hasGoal) return expectedHint(kpi, goal, period, print);
  if (key === 'upcoming') return upcomingHint(period, print);
  return undefined;
}

/**
 * `goal`: meta de agendamentos por mês (null sem meta). Vira a dica do card
 * "Previsto" (realizados + por vir) e a linha "Meta" no gráfico por mês.
 */
const ScheduleSection = ({ data, granularity, period, goal }) => {
  const print = useStatsPrint();
  return (
  <>
    <div className={styles.grid}>
      {SCHEDULE_CARDS.map(({ goal: hasGoal, ...card }) => (
        <StatCard
          key={card.key}
          {...card}
          kpi={data.kpis[card.key]}
          hint={cardHint(card.key, hasGoal, data.kpis[card.key], goal, period, print)}
        />
      ))}
      <StatCard
        label="Agendamentos criados no período"
        icon={<AddTaskOutlinedIcon />}
        accent="teal"
        kpi={data.created}
        help={HELP.created}
      />
      {data.occupancy && (
        <>
          <StatCard
            label="Ocupação da agenda"
            icon={<DonutLargeOutlinedIcon />}
            accent="blue"
            type="percent"
            kpi={data.occupancy.occupancy}
            hint={`${formatNumber(data.occupancy.hours.occupied, 1)} h de ${formatNumber(data.occupancy.hours.capacity, 1)} h disponíveis, até hoje`}
          />
          <StatCard
            label="Perdido com faltas"
            icon={<TimerOffOutlinedIcon />}
            accent="amber"
            type="percent"
            inverse
            kpi={data.occupancy.noshowLoss}
            hint={`${formatNumber(data.occupancy.hours.lost, 1)} h da agenda`}
          />
        </>
      )}
    </div>

    <div className={styles.row}>
      <ChartBlock title="Atendimentos" hint={granularityHint(granularity)}>
        <TimelineChart
          data={data.timeline}
          granularity={granularity}
          series={SCHEDULE_SERIES}
          total={SCHEDULE_TOTAL}
          goal={goal}
        />
      </ChartBlock>
      <ChartBlock title="Como terminaram" hint={rescheduledHint(data.outcomes.rescheduled)}>
        <DonutChart items={outcomeItems(data.outcomes)} centerLabel="horários" />
      </ChartBlock>
    </div>

    <div className={styles.rowEven}>
      <ChartBlock title="Por profissional" hint="realizados e por vir">
        <RankList items={resourceItems(data.byResource)} color={palette.blue} />
      </ChartBlock>
      <ChartBlock title="Procedimentos mais feitos">
        <RankList items={data.byProcedure.map((item) => ({ label: item.label, value: item.count }))} />
      </ChartBlock>
    </div>

    <ChartBlock title="Horários mais procurados" hint="dia da semana e hora">
      <Heatmap cells={data.heatmap} />
    </ChartBlock>
  </>
  );
};

export default ScheduleSection;
